<script>
  import { router } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import { Input } from '$lib/components/ui/input/index.js'
  import { Card, CardHeader, CardContent, CardFooter } from '$lib/components/ui/card/index.js'
  import RotateCcwIcon from '@lucide/svelte/icons/rotate-ccw'

  let { flash = {}, turnstile_site_key = '' } = $props()

  let authError = $derived(flash.auth_error ?? null)
  let rateLimitedUntil = $derived(flash.rate_limited_until ? new Date(flash.rate_limited_until) : null)

  let email = $state('')
  const isDev = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
  let cfToken = $state(isDev ? 'dev-bypass' : '')
  let submitting = $state(false)
  let turnstileReady = $state(isDev)

  let secondsLeft = $state(0)
  let countdownInterval = $state(null)

  $effect(() => {
    if (authError === 'rate_limited' && rateLimitedUntil) {
      const tick = () => {
        const diff = Math.max(0, Math.floor((rateLimitedUntil - Date.now()) / 1000))
        secondsLeft = diff
        if (diff <= 0) clearInterval(countdownInterval)
      }
      tick()
      countdownInterval = setInterval(tick, 1000)
      return () => clearInterval(countdownInterval)
    }
  })

  $effect(() => {
    if (authError === 'rate_limited') return
    const mountWidget = () => {
      if (typeof window.turnstile === 'undefined') return
      const el = document.getElementById('cf-turnstile')
      if (!el || el.dataset.mounted) return
      el.dataset.mounted = '1'
      window.turnstile.render(el, {
        sitekey: turnstile_site_key,
        theme: 'light',
        callback: (token) => { cfToken = token; turnstileReady = true },
        'expired-callback': () => { cfToken = ''; turnstileReady = false },
        'error-callback': () => { cfToken = ''; turnstileReady = false },
      })
    }
    const startPolling = () => {
      let attempts = 0
      const poll = setInterval(() => {
        attempts++
        if (typeof window.turnstile !== 'undefined') {
          clearInterval(poll)
          mountWidget()
        } else if (attempts > 30) {
          clearInterval(poll)
        }
      }, 200)
      return () => clearInterval(poll)
    }
    if (document.readyState === 'complete') {
      return startPolling()
    }
    const onLoad = () => startPolling()
    window.addEventListener('load', onLoad)
    return () => window.removeEventListener('load', onLoad)
  })

  function handleSubmit(e) {
    e.preventDefault()
    if (!cfToken || submitting) return
    submitting = true
    router.post('/login', { email, cf_turnstile_response: cfToken }, {
      onFinish: () => { submitting = false }
    })
  }

  function formatTime(s) {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m}:${sec.toString().padStart(2, '0')}`
  }
</script>

<svelte:head>
  <title>Log In — MyRentalTax</title>
</svelte:head>

<div class="min-h-dvh flex flex-col items-center justify-center gap-5 px-5 py-8 bg-[#fafafa]">
  <!-- Logo -->
  <div class="flex items-center gap-3">
    <div class="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-900 rounded-[10px] sm:rounded-xl flex items-center justify-center text-white flex-shrink-0">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/>
      </svg>
    </div>
    <span class="text-2xl sm:text-3xl font-bold text-zinc-900">MyRentalTax</span>
  </div>
  <p class="text-[13px] sm:text-base text-zinc-500 -mt-2">Malaysian Rental Income Tax Wizard — YA 2025</p>

  <!-- ── State: Rate Limited ── -->
  {#if authError === 'rate_limited'}
    <Card class="w-full max-w-sm sm:max-w-[480px] shadow-sm">
      <CardHeader class="px-5 pt-5 pb-0 sm:px-6 sm:pt-6 gap-1">
        <h2 class="text-lg sm:text-xl font-semibold text-zinc-900 m-0">Too Many Attempts</h2>
        <p class="text-sm text-zinc-500 leading-relaxed m-0">You've exceeded the maximum number of OTP requests. Please wait before trying again.</p>
      </CardHeader>
      <CardContent class="px-5 py-5 sm:px-6 flex flex-col items-center gap-4">
        <div class="w-24 h-24 rounded-full bg-zinc-100 flex flex-col items-center justify-center gap-1 text-zinc-400">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <span class="text-base font-semibold text-zinc-900">{formatTime(secondsLeft)}</span>
        </div>
        <p class="text-sm text-zinc-500 leading-relaxed text-center m-0">
          Please wait 5 minutes before requesting another OTP. This helps us prevent abuse and keep your account secure.
        </p>
      </CardContent>
      <CardFooter class="px-5 pb-5 sm:px-6 sm:pb-6 flex justify-end border-t border-zinc-100 pt-4">
        <Button disabled>Try Again</Button>
      </CardFooter>
    </Card>

  <!-- ── State: Turnstile Failed ── -->
  {:else if authError === 'turnstile_failed'}
    <Card class="w-full max-w-sm sm:max-w-[480px] shadow-sm">
      <CardHeader class="px-5 pt-5 pb-0 sm:px-6 sm:pt-6 gap-1">
        <h2 class="text-lg sm:text-xl font-semibold text-zinc-900 m-0">Verification Failed</h2>
        <p class="text-sm text-zinc-500 leading-relaxed m-0">We couldn't verify that you're human. This may happen due to browser settings or network issues.</p>
      </CardHeader>
      <CardContent class="px-5 py-5 sm:px-6 flex flex-col items-center gap-4">
        <div class="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-red-500">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <div class="flex items-center gap-2.5 h-16 px-3 rounded bg-red-50 border border-red-400 w-full max-w-[300px] box-border">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-red-500 flex-shrink-0"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          <span class="flex-1 text-sm font-medium text-red-500">Verification failed</span>
          <div class="flex flex-col items-center gap-0.5 w-10">
            <span class="text-[#f48120] text-2xl leading-none">☁</span>
            <span class="text-[#999] text-[8px] leading-none">Cloudflare</span>
          </div>
        </div>
        <p class="text-sm text-zinc-500 leading-relaxed text-center m-0">
          Please click the button below to try the verification again. If the problem persists, try refreshing the page or using a different browser.
        </p>
      </CardContent>
      <CardFooter class="px-5 pb-5 sm:px-6 sm:pb-6 flex justify-end border-t border-zinc-100 pt-4">
        <Button href="/login">
          <RotateCcwIcon class="w-4 h-4" />
          Try Again
        </Button>
      </CardFooter>
    </Card>

  <!-- ── State: Default — Email Entry ── -->
  {:else}
    <form class="w-full max-w-sm sm:max-w-[480px]" onsubmit={handleSubmit}>
      <Card class="shadow-sm">
        <CardHeader class="px-5 pt-5 pb-0 sm:px-6 sm:pt-6 gap-1">
          <h2 class="text-lg sm:text-xl font-semibold text-zinc-900 m-0">Log In</h2>
          <p class="text-sm text-zinc-500 leading-relaxed m-0">Enter your email address to receive a one-time PIN.</p>
        </CardHeader>
        <CardContent class="px-5 py-5 sm:px-6 flex flex-col gap-4">
          <div class="flex flex-col gap-1.5">
            <label for="email" class="text-sm font-medium text-zinc-900">Email Address</label>
            <Input
              id="email"
              type="email"
              name="email"
              placeholder="you@example.com"
              required
              autocomplete="email"
              bind:value={email}
              class="h-9"
            />
          </div>

          {#if isDev}
            <div class="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded px-2 py-1">
              🔧 Dev mode — Turnstile bypassed
            </div>
          {:else}
            <div id="cf-turnstile"></div>
          {/if}

          <p class="text-xs text-zinc-400 m-0">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </CardContent>
        <CardFooter class="px-5 pb-5 sm:px-6 sm:pb-6 flex justify-end border-t border-zinc-100 pt-4">
          <Button type="submit" disabled={!cfToken || submitting}>
            {submitting ? 'Sending…' : 'Send OTP'}
          </Button>
        </CardFooter>
      </Card>
    </form>
  {/if}
</div>
